import React, { useContext, useEffect } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router-dom";

import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import { FormInputField } from "@components-shared/desktop/inputs";
import { Button } from "@components/buttons";
import { DepositProcess } from "@data-types";
import {
	DepositBonusConfirmation,
	DepositMultiStepFooter,
	DepositPasswordConfirmation,
	DepositPaymentMethodSelection,
	DepositWalletSelector,
	SelectedDepositAmount,
	SelectedDepositPaymentMethod,
	SelectedDepositWallet,
} from "@features/payment/deposit/desktop/new-deposit";
import { useRootAppStore } from "@hooks";
import { DepositContext } from "@state/context/payment/DepositContext";
import { useAppContext } from "@state/hooks/shared";
import { getCurrentCulture } from "@utils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { isBankIdPasswordlessUser } from "@utils/specific/bankid/bankIdStepUp";
import { observer } from "mobx-react-lite";

export default observer(function NewDepositBody() {
	const { t } = useTranslation(["payment", "user-account"]);

	const { gracePeriodStore, userAuthStore } = useRootAppStore();

	const { shouldShowDataConfirmationEmptyState } =
		userAuthStore.userDataConfirmationStore;

	const {
		initializeNewDepositProcess,
		finalizeMultiStepDeposit,
		isPasswordConfirmation,
		isBonusConfirmation,
		isMultiStepDeposit,
		isStoreInitialized,
		onlineWallet,
		gamesWallet,
		paymentMethods,
		resetMultiStepFlags,
		isRedirectingToPaymentPage,
		isLoadingProcess,
		paymentMethodError,
		shouldShowWalletSelectors,
		partialDepositAmount,
		setPartialDepositAmount,
	} = useContext(DepositContext);

	const methods = useForm({
		defaultValues: {
			bettingAccountTypeId:
				onlineWallet?.accountTypeId || gamesWallet?.accountTypeId,
			paymentMethodId: "",
			amount: "",
			isBonusAccepted: false,
		},
	});

	const { handleSubmit, getValues, watch, trigger, setValue } = methods;

	useEffect(() => {
		return () => {
			resetMultiStepFlags();
		};
	}, []);

	useEffect(() => {
		if (partialDepositAmount) {
			setValue("amount", partialDepositAmount.toString());
		}
	}, [partialDepositAmount]);

	useEffect(() => {
		if (
			isStoreInitialized &&
			getValues("paymentMethodId") != "" &&
			getValues("amount") != ""
		) {
			trigger("amount");
		}
	}, [watch("paymentMethodId")]);

	if (shouldShowDataConfirmationEmptyState) {
		return (
			<div className="at-depositFormMultiPwd card--primary">
				<div className="card--onboarding__content card--onboarding__content--grow">
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("DEPOSIT.TITLE")}
						</h1>
					</div>
					<DepositTabSwitcher />
					<div className="card--primary__body">
						<div className="card--empty">
							<div className="card--empty__content">
								<i className="u-icon u-icon--xxlrg u-icon--deposit--alt u-mar--bottom--sml"></i>
								<p className="u-mar--bottom--sml">
									{t(
										"user-account:ONLINE_USER_DATA_CONFIRMATION_REQUIRED"
									)}
								</p>
								<div>
									<a
										className="btn btn--med btn--primary--inverted u-mar--right--sml"
										onClick={() =>
											App.state.redirect(
												`/${getCurrentCulture()}/${DefaultPath.HOME}`
											)
										}
									>
										{t("COMMON.BACK_TO_HOME_PAGE")}
									</a>
									<a
										className="btn btn--med btn--primary"
										onClick={
											userAuthStore
												.userDataConfirmationStore
												.redirectToDataConfirmation
										}
									>
										{t("user-account:CONFIRM")}
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (gracePeriodStore.shouldShowGracePeriodEmptyState) {
		return (
			<div className="at-depositFormMultiPwd card--primary">
				<div className="card--onboarding__content card--onboarding__content--grow">
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("DEPOSIT.TITLE")}
						</h1>
					</div>
					<DepositTabSwitcher />
					<div className="card--primary__body">
						<div className="card--empty">
							<div className="card--empty__content">
								<i className="u-icon u-icon--xxlrg u-icon--deposit--alt u-mar--bottom--sml"></i>
								<p className="u-mar--bottom--sml">
									{t(
										"USER.POPUPS.GRACE_PERIOD.EMPTY_STATE.MESSAGE"
									)}
								</p>
								<div>
									<a
										className="btn btn--med btn--primary--inverted u-mar--right--sml"
										onClick={() =>
											App.state.redirect(
												`/${getCurrentCulture()}/${DefaultPath.HOME}`
											)
										}
									>
										{t("COMMON.BACK_TO_HOME_PAGE")}
									</a>
									<a
										className="btn btn--med btn--primary"
										onClick={gracePeriodStore.showPopup}
									>
										{t(
											"USER.POPUPS.GRACE_PERIOD.EMPTY_STATE.CONFIRM_TITLE"
										)}
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (paymentMethodError) {
		return (
			<div className="at-depositFormMultiPwd card--primary">
				<div className="card--primary__header">
					<h1 className="card--primary__title title--primary">
						{t<string>("DEPOSIT.TITLE")}
					</h1>
				</div>
				<DepositTabSwitcher />
				<div className="card--primary__body">
					<InfoMessageComponent
						type="warning"
						message={paymentMethodError.message}
						textClass="message__text"
					/>
				</div>
				<div className="card--primary__footer">
					<DepositErrorMessageFooter
						errorCode={paymentMethodError.errorCode}
					/>
				</div>
			</div>
		);
	}

	const onSubmit = async (data: DepositProcess) => {
		setPartialDepositAmount(null);
		initializeNewDepositProcess(data);
		window.scrollTo(0, 0);
	};

	const onMultiStepSubmit = async (data: {
		isBonusAccepted?: boolean;
		password?: string;
		bettingAccountTypeId?: string;
	}) => {
		finalizeMultiStepDeposit(data);
	};

	if (isMultiStepDeposit && isPasswordConfirmation && isBonusConfirmation) {
		return (
			<FormProvider {...methods}>
				<form
					id="multiStepForm"
					className="at-depositFormMultiPwdBonus card--primary"
					onSubmit={handleSubmit(onMultiStepSubmit)}
				>
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("DEPOSIT.TITLE")}
						</h1>
					</div>

					<DepositTabSwitcher />

					<div className="card--primary__body">
						<div className="form__section">
							{shouldShowWalletSelectors && (
								<div className="form__group">
									<SelectedDepositWallet />
								</div>
							)}
							<div className="form__group">
								<SelectedDepositPaymentMethod />
							</div>
							<div className="form__group">
								<SelectedDepositAmount />
							</div>
						</div>

						<div className="form__section">
							<DepositBonusConfirmation />
						</div>

						<div className="form__section">
							{!isBankIdPasswordlessUser(userAuthStore.user) && (
								<DepositPasswordConfirmation />
							)}
						</div>
					</div>

					<div className="card--primary__footer">
						<DepositMultiStepFooter />
					</div>
				</form>
			</FormProvider>
		);
	}

	if (isMultiStepDeposit && isPasswordConfirmation) {
		return (
			<FormProvider {...methods}>
				<form
					id="multiStepForm"
					className="at-depositFormMultiPwd card--primary"
					onSubmit={handleSubmit(onMultiStepSubmit)}
				>
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("DEPOSIT.TITLE")}
						</h1>
					</div>

					<DepositTabSwitcher />

					<div className="card--primary__body">
						<div className="form__section">
							{shouldShowWalletSelectors && (
								<div className="form__group">
									<SelectedDepositWallet />
								</div>
							)}
							<div className="form__group">
								<SelectedDepositPaymentMethod />
							</div>
							<div className="form__group">
								<SelectedDepositAmount />
							</div>
						</div>

						<div className="form__section">
							{!isBankIdPasswordlessUser(userAuthStore.user) && (
								<DepositPasswordConfirmation />
							)}
						</div>
					</div>

					<div className="card--primary__footer">
						<DepositMultiStepFooter />
					</div>
				</form>
			</FormProvider>
		);
	}

	if (isMultiStepDeposit && isBonusConfirmation) {
		return (
			<FormProvider {...methods}>
				<form
					id="multiStepForm"
					className="at-depositFormMultiBonus card--primary"
					onSubmit={handleSubmit(onMultiStepSubmit)}
				>
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("DEPOSIT.TITLE")}
						</h1>
					</div>

					<DepositTabSwitcher />

					<div className="card--primary__body">
						<div className="form__section">
							{shouldShowWalletSelectors && (
								<div className="form__group">
									<SelectedDepositWallet />
								</div>
							)}
							<div className="form__group">
								<SelectedDepositPaymentMethod />
							</div>
							<div className="form__group">
								<SelectedDepositAmount />
							</div>
						</div>

						<div className="form__section">
							<DepositBonusConfirmation />
						</div>
					</div>

					<div className="card--primary__footer">
						<DepositMultiStepFooter />
					</div>
				</form>
			</FormProvider>
		);
	}

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="card--primary">
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("DEPOSIT.TITLE")}
						</h1>
					</div>

					<DepositTabSwitcher />

					<div className="card--primary__body">
						{shouldShowWalletSelectors ? (
							<section className="form__section">
								<div className="u-mar--bottom--med u-type--med">
									{t("DEPOSIT.SELECT_DEPOSIT_WALLET")}
								</div>

								<span className="label">
									{t("COMMON.MY_WALLETS")}
								</span>
								<DepositWalletSelector />
							</section>
						) : (
							<DepositWalletSelector />
						)}

						<section className="form__section">
							<div className="u-mar--bottom--med u-type--med">
								{t<string>("DEPOSIT.SELECT_PAYMENT_METHOD")}
							</div>
							<DepositPaymentMethodSelection />
						</section>

						<section className="form__section">
							<div className="u-mar--bottom--med u-type--med">
								{t("DEPOSIT.DEPOSIT_MONEY_TO_ACCOUNT")}
							</div>

							<FormInputField
								required
								label={"payment:DEPOSIT.DEPOSIT_MONEY"}
								name="amount"
								className="form__group"
								type="text"
								pattern={{
									value: /^\d+(\.\d{1,2})?$/,
									message: t(
										"CASINO.BALANCE_TRANSFER.AMOUNT_VALIDATION_ERROR"
									),
								}}
								placeholder="payment:COMMON.AMOUNT_PLACEHOLDER"
								validate={{
									isInRange: (value: number) => {
										const paymentMethod =
											paymentMethods?.find(
												(pm: {
													paymentMethodId: string;
												}) =>
													pm.paymentMethodId ===
													getValues("paymentMethodId")
											);

										const maxDeposit =
											partialDepositAmount ||
											paymentMethod?.maxDeposit ||
											0;

										return (
											(paymentMethod?.minDeposit !=
												null &&
												value >=
													paymentMethod?.minDeposit &&
												value <= maxDeposit) ||
											t<string>(
												"DEPOSIT.DEPOSIT_MONEY_NOT_IN_RANGE",
												{
													0: paymentMethod?.minDeposit,
													1: maxDeposit,
												}
											)
										);
									},
								}}
							/>
						</section>
					</div>

					<div className="card--primary__footer">
						<Button
							className="card--primary__footer__btn btn--base btn--accent btn--icon"
							type="submit"
							disabled={
								isLoadingProcess ||
								isRedirectingToPaymentPage ||
								paymentMethods == null ||
								paymentMethods.length === 0
							}
							btnText={t<string>("COMMON.CONTINUE_BTN")}
							iconClassName="u-icon u-icon--tny u-icon--arrow-right u-color--accent--text"
						/>
					</div>
				</div>
			</form>
		</FormProvider>
	);
});

const DepositErrorMessageFooter = function DepositErrorMessageFooter(props: {
	errorCode: number;
}) {
	const { t } = useTranslation([
		"account-verification",
		"account-settings-navigation",
	]);
	const { errorCode } = props;

	if (errorCode === 14) {
		return (
			<Link
				to={`account-settings/account-verification`}
				className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
			>
				{t("TITLE")}
			</Link>
		);
	} else if (errorCode === 15) {
		return (
			<Link
				to={`account-settings/my-limits`}
				className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
			>
				{t("account-settings-navigation:MY_LIMITS")}
			</Link>
		);
	} else if (errorCode === 69) {
		return (
			<Link
				to="user-profile-recheck"
				className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
			>
				{t("ACCOUNT_RECHECK.TITLE")}
			</Link>
		);
	}

	return (
		<Link
			to={`info/contact`}
			className="card--primary__footer__btn btn btn--base btn--accent btn--icon"
		>
			{t("FOOTER.CONTACT")}
		</Link>
	);
};

const DepositTabSwitcher = observer(function DepositTabSwitcher() {
	const { t } = useTranslation("payment");
	const { culture } = useAppContext();

	return (
		<div className="card--primary__tabs u-padd--x--reset">
			<nav className="tabs--primary">
				<NavLink
					className="tabs--primary__item"
					activeClassName="is-active"
					replace={true}
					to={`/${culture}/deposit`}
					isActive={(match, location) => {
						return (
							location.pathname ==
							`/${getCurrentCulture()}/deposit`
						);
					}}
				>
					{t<string>("DEPOSIT.NEW_DEPOSIT")}
				</NavLink>
				<NavLink
					to={`/${culture}/deposit/history`}
					className="tabs--primary__item"
					replace={true}
					activeClassName="is-active"
					isActive={(match, location) => {
						return (
							location.pathname ==
							`/${getCurrentCulture()}/deposit/history`
						);
					}}
				>
					{t<string>("DEPOSIT.MY_DEPOSITS")}
				</NavLink>
			</nav>
		</div>
	);
});
